import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/* Master legal compliance center. Tab-based single-page renderer for
 * Terms, Privacy, Community Guidelines, Disclaimer, Contact.
 * Route: /legal?tab=terms|privacy|community|disclaimer|contact
 */
const SECTIONS = [
  { key: "terms",       label: "Terms of Service" },
  { key: "privacy",     label: "Privacy Policy" },
  { key: "community",   label: "Community Guidelines" },
  { key: "disclaimer",  label: "Disclaimer" },
  { key: "contact",     label: "Contact & Support" },
];

export default function LegalPage() {
  const initial = new URLSearchParams(window.location.search).get("tab") || "terms";
  const [tab, setTab] = useState(initial);

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <Link to="/" className="font-heading font-black text-xl">Tainapp</Link>
          <Link to="/" className="text-xs text-white/60 hover:text-white">← Back home</Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 grid md:grid-cols-[220px_1fr] gap-6">
        <nav className="md:sticky md:top-6 self-start" data-testid="legal-nav">
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
            {SECTIONS.map(s => (
              <button
                key={s.key}
                data-testid={`legal-tab-${s.key}`}
                onClick={() => setTab(s.key)}
                className={`text-left text-sm px-3 py-2 rounded-md whitespace-nowrap transition ${
                  tab === s.key ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "text-white/70 hover:bg-white/5"
                }`}
              >{s.label}</button>
            ))}
          </div>
        </nav>

        <Card className="bg-[#111] border-white/10">
          <CardContent className="p-6 md:p-8 space-y-6 leading-relaxed text-sm text-white/85" data-testid={`legal-section-${tab}`}>
            {tab === "terms" && <TermsSection />}
            {tab === "privacy" && <PrivacySection />}
            {tab === "community" && <CommunitySection />}
            {tab === "disclaimer" && <DisclaimerSection />}
            {tab === "contact" && <ContactSection />}
            <div className="pt-4 border-t border-white/10 text-xs text-white/50">
              © 2026 Tainapp Inc. All rights reserved.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function H({ children }) {
  return <h2 className="font-heading text-xl font-black text-white mt-2">{children}</h2>;
}
function H3({ children }) {
  return <h3 className="font-heading font-bold text-white mt-3">{children}</h3>;
}

function TermsSection() {
  return (
    <>
      <H>Section A: Terms of Service</H>
      <H3>1. Acceptance of Terms</H3>
      <p>By accessing or using Tainapp ("Platform"), operating under Tainapp Inc., you agree to be bound by these Terms of Service. If you do not agree to all terms, you must refrain from using the Platform.</p>
      <H3>2. Description of Services</H3>
      <p>Tainapp provides a multi-service digital ecosystem encompassing: Creator Content Feeds, School Finding &amp; Management Hubs, Business Partnerships &amp; Marketplaces, Job Board &amp; Professional Candidate Profiles, Interactive Platform Academic Games, and Real-Time Messaging &amp; Find Friends Hub.</p>
      <H3>3. Account Eligibility &amp; Registration</H3>
      <p>Users must provide accurate, complete registration information. You are responsible for safeguarding your login credentials. Tainapp reserves the right to suspend or terminate accounts that provide false information or violate platform rules.</p>
      <H3>4. Credits &amp; Platform Economy</H3>
      <ul className="list-disc list-inside space-y-1">
        <li>Platform currency ("Credits" or "Points") operates on a base conversion rate managed in the Admin Credits Economy tab.</li>
        <li>Credits spent on engagement, profile promotion, specific school views, parent/student portal access, or feature access are non-refundable digital assets.</li>
        <li>Teacher and Headteacher management portal access remains completely credit-free.</li>
        <li>Content Creator revenue shares are calculated and distributed automatically based on platform rates.</li>
      </ul>
      <H3>5. Governing Law &amp; Jurisdiction</H3>
      <p>These Terms are governed by and construed in accordance with the laws of the Republic of Ghana. Any disputes arising from the use of Tainapp shall be subject to the exclusive jurisdiction of the courts of Ghana.</p>
    </>
  );
}

function PrivacySection() {
  return (
    <>
      <H>Section B: Privacy Policy</H>
      <H3>1. Information We Collect</H3>
      <p>In compliance with the Data Protection Act, 2012 (Act 843) of Ghana, Tainapp collects:</p>
      <ul className="list-disc list-inside space-y-1">
        <li><strong>Personal Identifiable Information</strong>: Name, email, phone number, occupation, and physical profile data during account registration or Find Friends profiling.</li>
        <li><strong>Usage &amp; Analytics</strong>: IP addresses, device information, content interaction metrics, and transaction logs.</li>
      </ul>
      <H3>2. Purpose of Data Processing</H3>
      <p>We process your personal data strictly to authenticate user identity, facilitate direct communications and social connections, process credit transactions, and prevent fraud.</p>
      <H3>3. Data Protection &amp; Security</H3>
      <p>Tainapp implements physical, electronic, and procedural safeguards to secure user data against unauthorized access, loss, or alteration. Tainapp does not sell or rent personal data to third-party ad networks.</p>
      <H3>4. User Rights</H3>
      <p>Users retain the right to request access to their personal data, correct inaccuracies, or request account deletion by contacting support.</p>
    </>
  );
}

function CommunitySection() {
  return (
    <>
      <H>Section C: Community Guidelines</H>
      <H3>1. Safe &amp; Respectful Environment</H3>
      <p>Tainapp is built to foster growth, education, and community coordination. Users must interact with integrity, civil respect, and fairness across all feeds, message boards, matchmaking hubs, and academic competitions.</p>
      <H3>2. Prohibited Content &amp; Conduct</H3>
      <p>Users are strictly prohibited from posting defamatory/violent speech, unauthorized copyrighted media, fraudulent job offers, fake matchmaking profiles, or using automated bots/spam to manipulate voting, credits, or engagement.</p>
      <H3>3. Enforcement &amp; Reporting</H3>
      <p>Tainapp uses automated content filters, in-app feed report overlays, and community flags. Violations may result in content removal, account suspension, or permanent expulsion.</p>
    </>
  );
}

function DisclaimerSection() {
  return (
    <>
      <H>Section D: Platform Disclaimer</H>
      <H3>1. Independent Platform Infrastructure</H3>
      <p>Tainapp serves as a technical facilitator connecting users, creators, businesses, job seekers, friends, and educational institutions. Tainapp Inc. does not act as an employer, educational accreditation body, financial institution, or legal guarantor.</p>
      <H3>2. Third-Party Content &amp; Links</H3>
      <p>Tainapp is not liable for user-generated content, external links, job listings, friend matchmaking interactions, or third-party merchant products hosted on the marketplace.</p>
    </>
  );
}

function ContactSection() {
  return (
    <>
      <H>Section E: Contact &amp; Support</H>
      <ul className="space-y-2 text-white/85">
        <li><strong>Customer Support Line</strong>: <a href="tel:+233539025750" className="text-emerald-300 underline">+233 53 902 5750</a></li>
        <li><strong>Physical Corporate Address</strong>: Tainapp Technologies, Pokuase-Awoshie Road, Baabs Junction, Accra, Ghana.</li>
        <li><strong>In-App Helpdesk</strong>: Access the "Support Ticket" feature within your profile menu or Inbox Drawer.</li>
      </ul>
      <div className="pt-4">
        <a href="https://wa.me/233539025750" target="_blank" rel="noreferrer">
          <Button data-testid="legal-contact-whatsapp" className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">Contact us on WhatsApp</Button>
        </a>
      </div>
    </>
  );
}
